import { type AdifRecordType } from '../types/AdifRecordType';

const fieldTypeMap: Record<keyof AdifRecordType, string> = {
  appHamrsMyClass: 'string',
  address: 'string',
  addressIntl: 'string',
  age: 'number',
  altitude: 'number',
  antAz: 'number',
  antEl: 'number',
  antPath: 'string',
  arrlSect: 'string',
  awardGranted: 'string',
  awardSubmitted: 'string',
  aIndex: 'number',
  band: 'string',
  bandRx: 'string',
  call: 'string',
  check: 'string',
  class: 'string',
  clublogQsoUploadDate: 'date',
  clublogQsoUploadStatus: 'string',
  cnty: 'string',
  comment: 'string',
  commentIntl: 'string',
  cont: 'string',
  contactedOp: 'string',
  contestId: 'string',
  country: 'string',
  countryIntl: 'string',
  cqz: 'number',
  creditSubmitted: 'string',
  creditGranted: 'string',
  darcDok: 'string',
  distance: 'number',
  dxcc: 'number',
  email: 'string',
  eqCall: 'string',
  eqslQslrdate: 'date',
  eqslQslsdate: 'date',
  eqslQslRcvd: 'string',
  eqslQslSent: 'string',
  fists: 'number',
  fistsCc: 'number',
  forceInit: 'string',
  freq: 'number',
  freqRx: 'number',
  gridsquare: 'string',
  gridsquareExt: 'string',
  guestOp: 'string',
  hamlogeuQsoUploadDate: 'date',
  hamlogeuQsoUploadStatus: 'string',
  hamqthQsoUploadDate: 'date',
  hamqthQsoUploadStatus: 'string',
  hrdlogQsoUploadDate: 'date',
  hrdlogQsoUploadStatus: 'string',
  iota: 'string',
  iotaIslandId: 'number',
  ituz: 'number',
  kIndex: 'number',
  lat: 'number',
  lon: 'number',
  lotwQslrdate: 'date',
  lotwQslsdate: 'date',
  lotwQslRcvd: 'string',
  lotwQslSent: 'string',
  maxBursts: 'number',
  mode: 'string',
  msShower: 'string',
  myAltitude: 'number',
  myAntenna: 'string',
  myAntennaIntl: 'string',
  myArrlSect: 'string',
  myCity: 'string',
  myCityIntl: 'string',
  myCnty: 'string',
  myCountry: 'string',
  myCountryIntl: 'string',
  myCqZone: 'number',
  myDxcc: 'number',
  myFists: 'number',
  myGridsquare: 'string',
  myGridsquareExt: 'string',
  myIota: 'string',
  myIotaIslandId: 'number',
  myItuZone: 'number',
  myLat: 'number',
  myLon: 'number',
  myName: 'string',
  myNameIntl: 'string',
  myPostalCode: 'string',
  myPostalCodeIntl: 'string',
  myPotaRef: 'string',
  myRig: 'string',
  myRigIntl: 'string',
  mySig: 'string',
  mySigIntl: 'string',
  mySigInfo: 'string',
  mySigInfoIntl: 'string',
  mySotaRef: 'string',
  myState: 'string',
  myStreet: 'string',
  myStreetIntl: 'string',
  myUsacaCounties: 'string',
  myVuccGrids: 'string',
  myWwffRef: 'string',
  name: 'string',
  nameIntl: 'string',
  notes: 'string',
  notesIntl: 'string',
  nrBursts: 'number',
  nrPings: 'number',
  operator: 'string',
  ownerCallsign: 'string',
  pfx: 'string',
  potaRef: 'string',
  precedence: 'string',
  propMode: 'string',
  publicKey: 'string',
  qrzcomQsoUploadDate: 'date',
  qrzcomQsoUploadStatus: 'string',
  qslmsg: 'string',
  qslmsgIntl: 'string',
  qslrdate: 'date',
  qslsdate: 'date',
  qslRcvd: 'string',
  qslRcvdVia: 'string',
  qslSent: 'string',
  qslSentVia: 'string',
  qslVia: 'string',
  qsoComplete: 'string',
  qsoDate: 'string',
  qsoDateOff: 'string',
  qsoRandom: 'boolean',
  qth: 'string',
  qthIntl: 'string',
  region: 'string',
  rig: 'string',
  rigIntl: 'string',
  rstRcvd: 'string',
  rstSent: 'string',
  rxPwr: 'number',
  satMode: 'string',
  satName: 'string',
  sfi: 'number',
  sig: 'string',
  sigIntl: 'string',
  sigInfo: 'string',
  sigInfoIntl: 'string',
  silentKey: 'boolean',
  skcc: 'string',
  sotaRef: 'string',
  srx: 'number',
  srxString: 'string',
  state: 'string',
  stationCallsign: 'string',
  stx: 'number',
  stxString: 'string',
  submode: 'string',
  swl: 'boolean',
  tenTen: 'number',
  timeOff: 'string',
  timeOn: 'string',
  txPwr: 'number',
  uksmg: 'number',
  usacaCounties: 'string',
  veProv: 'string',
  vuccGrids: 'string',
  web: 'string',
  wwffRef: 'string'
};

export function convertAdifToJson(adif: string): AdifRecordType[] {
  const adifBody = extractAdifBody(adif);
  const rawRecords = splitRecords(adifBody);
  const parsedRecords = rawRecords.map(parseRecord).filter(isValidRecord);
  return parsedRecords;
}

// Extracts the ADIF body after the header (<EOH>)
function extractAdifBody(adif: string): string {
  return adif.split(/<eoh>/i)[1]?.trim() || '';  // Case-insensitive <EOH>
}

// Splits the ADIF body into individual records using <EOR> as a delimiter
function splitRecords(body: string): string[] {
  return body.split(/<eor>/i).map(record => record.trim()).filter(record => record.length > 0);  // Case-insensitive <EOR>
}

// Parses an individual ADIF record into an AdifRecordType object
function parseRecord(record: string): AdifRecordType {
  const fields = record.split('<').filter(field => field.trim());
  const entry: Partial<AdifRecordType> = {};

  fields.forEach(field => {
    const [keyWithLength, value] = field.split('>');
    if (keyWithLength && value) {
      const key = keyWithLength.split(':')[0]; // Extract key, ignore length part
      if (!key.toLowerCase().startsWith('app')) {  // Skip fields that start with 'app_'
        const camelCaseKey = toCamelCase(key.toLowerCase()) as keyof AdifRecordType;
        entry[camelCaseKey] = parseValue(camelCaseKey, value.trim()) as any;
      }
    }
  });

  return entry as AdifRecordType;
}

// Parses the value based on the field type from fieldTypeMap
function parseValue(key: keyof AdifRecordType, value: string): string | number | boolean | Date {
  const fieldType = fieldTypeMap[key];

  switch (fieldType) {
    case 'number':
      return parseFloat(value);
    case 'date':
      return new Date(value);
    case 'boolean':
      return value === 'Y' || value === 'true';
    default:
      // Handle ADIF 3.1.6 county format: "STATE,County"
      // Extract just the county name for internal storage
      if ((key === 'cnty' || key === 'myCnty') && value.includes(',')) {
        const parts = value.split(',');
        if (parts.length >= 2 && parts[1].trim()) {
          return parts[1].trim();
        }
        // If format is invalid, return the original value
        return value;
      }
      return value;
  }
}

// Checks if a record is valid by ensuring it has required fields (e.g., call, qsoDate)
function isValidRecord(record: AdifRecordType): boolean {
  return !!record.call && !!record.qsoDate;
}

// Converts a string from underscore_case to camelCase
function toCamelCase(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}